import { System } from '@/ecs/System';
import type { World } from '@/ecs/World';
import type { Entity } from '@/ecs/Entity';
import { Position } from '@/ecs/Component';
import { Stats, Owner, CombatState, UnitMesh } from '@/ecs/components/UnitComponents';
import { TargetingSystem, TargetingStrategy } from './TargetingSystem';
import { PathfindingSystem } from './PathfindingSystem';
import {
  calculatePhysicalDamage,
  applyDamage,
  getAttackCooldown,
} from './DamageCalculation';
import { coordToKey } from '@/hex/HexCoord';
import type { HexGrid } from '@/hex/HexGrid';
import type { UnitRenderer } from '@/units/UnitRenderer';
import type { EventBus } from '@/core/EventBus';

/**
 * Combat events
 */
export const CombatEvents = {
  UNIT_ATTACKED: 'combat:unitAttacked',
  UNIT_DAMAGED: 'combat:unitDamaged',
  UNIT_DIED: 'combat:unitDied',
  UNIT_MOVED: 'combat:unitMoved',
  COMBAT_STARTED: 'combat:started',
  COMBAT_ENDED: 'combat:ended',
} as const;

export interface UnitAttackedEvent {
  attackerId: string;
  targetId: string;
  damage: number;
  isCritical: boolean;
}

export interface UnitDiedEvent {
  entityId: string;
  killerId: string | null;
}

/**
 * Combat speed multipliers
 */
export enum CombatSpeed {
  Normal = 1,
  Fast = 2,
  VeryFast = 4,
}

/**
 * Combat system - handles auto-battle logic
 */
export class CombatSystem extends System {
  public readonly priority = 100;

  private hexGrid: HexGrid;
  private unitRenderer: UnitRenderer;
  private eventBus: EventBus;
  private targetingSystem!: TargetingSystem;
  private pathfindingSystem: PathfindingSystem;

  private isActive = false;
  private combatSpeed: CombatSpeed = CombatSpeed.Normal;
  private combatTime = 0;
  private dyingUnitsCount = 0; // Track units currently playing death animations

  constructor(hexGrid: HexGrid, unitRenderer: UnitRenderer, eventBus: EventBus) {
    super();
    this.hexGrid = hexGrid;
    this.unitRenderer = unitRenderer;
    this.eventBus = eventBus;
    this.pathfindingSystem = new PathfindingSystem(hexGrid);
  }

  override init(world: World): void {
    super.init(world);
    this.targetingSystem = new TargetingSystem(world);
  }

  /**
   * Start combat
   */
  startCombat(): void {
    this.isActive = true;
    this.combatTime = 0;
    this.dyingUnitsCount = 0;

    // Initialize combat state for all units
    const units = this.targetingSystem.getAllUnitsOnBoard();

    // Debug: Count units per player
    const p1Units = units.filter(u => u.getComponent(Owner)?.playerId === 1);
    const p2Units = units.filter(u => u.getComponent(Owner)?.playerId === 2);
    console.log(`[Combat] Starting combat with ${units.length} units total (P1: ${p1Units.length}, P2: ${p2Units.length})`);

    for (const unit of units) {
      const combatState = unit.getComponent(CombatState);
      if (combatState) {
        (combatState as { targetEntityId: string | null }).targetEntityId = null;
        (combatState as { attackCooldown: number }).attackCooldown = 0;
        (combatState as { isMoving: boolean }).isMoving = false;
        (combatState as { isDead: boolean }).isDead = false;
      }
    }

    this.eventBus.emit(CombatEvents.COMBAT_STARTED, {});
    console.log('Combat started');
  }

  /**
   * Stop combat
   */
  stopCombat(): void {
    this.isActive = false;
    const winner = this.getWinner();
    console.log(`[Combat] Combat ended after ${this.combatTime.toFixed(2)}s. Winner: ${winner === 0 ? 'Draw' : 'Player ' + winner}`);
    this.eventBus.emit(CombatEvents.COMBAT_ENDED, { time: this.combatTime });
  }

  /**
   * Force stop combat (server authority override)
   * Does NOT emit COMBAT_ENDED to avoid duplicate network messages
   */
  forceStop(): void {
    if (!this.isActive) return;
    this.isActive = false;
    console.log(`[Combat] Force stopped by server authority after ${this.combatTime.toFixed(2)}s`);
  }

  /**
   * Set combat speed
   */
  setCombatSpeed(speed: CombatSpeed): void {
    this.combatSpeed = speed;
  }

  /**
   * Check if combat is active
   */
  get active(): boolean {
    return this.isActive;
  }

  /**
   * Update combat each frame
   */
  override update(deltaTime: number): void {
    if (!this.isActive) return;

    // deltaTime is already in seconds from the game loop
    const scaledDelta = deltaTime * this.combatSpeed;
    this.combatTime += scaledDelta;

    // Process all living units
    const units = this.targetingSystem.getAllUnitsOnBoard();
    const livingUnits = units.filter((u) => {
      const cs = u.getComponent(CombatState);
      return cs && !cs.isDead;
    });

    // Debug: Log on first frame
    if (this.combatTime < 0.1) {
      console.log(`[Combat] First update - units on board: ${units.length}, living: ${livingUnits.length}`);
    }

    // Check win condition
    if (this.checkWinCondition(livingUnits)) {
      this.stopCombat();
      return;
    }

    // Process each unit
    for (const unit of livingUnits) {
      this.processUnit(unit, scaledDelta);
    }
  }

  /**
   * Check if combat should end
   */
  private checkWinCondition(livingUnits: Entity[]): boolean {
    const player1Units = livingUnits.filter((u) => {
      const owner = u.getComponent(Owner);
      return owner?.playerId === 1;
    });

    const player2Units = livingUnits.filter((u) => {
      const owner = u.getComponent(Owner);
      return owner?.playerId === 2;
    });

    const shouldEnd = player1Units.length === 0 || player2Units.length === 0;

    // Don't end combat if there are units still playing death animations
    if (shouldEnd && this.dyingUnitsCount > 0) {
      console.log(`[Combat] Win condition met but waiting for ${this.dyingUnitsCount} death animations to finish`);
      return false;
    }

    if (shouldEnd) {
      console.log(`[Combat] Win condition met: P1 units: ${player1Units.length}, P2 units: ${player2Units.length}, total living: ${livingUnits.length}`);
    }

    // Combat ends when one side has no units
    return shouldEnd;
  }

  /**
   * Get the winner (1, 2, or 0 for draw)
   */
  getWinner(): number {
    const units = this.targetingSystem.getAllUnitsOnBoard();
    const livingUnits = units.filter((u) => {
      const cs = u.getComponent(CombatState);
      return cs && !cs.isDead;
    });

    const player1Units = livingUnits.filter((u) => {
      const owner = u.getComponent(Owner);
      return owner?.playerId === 1;
    });

    const player2Units = livingUnits.filter((u) => {
      const owner = u.getComponent(Owner);
      return owner?.playerId === 2;
    });

    if (player1Units.length > 0 && player2Units.length === 0) return 1;
    if (player2Units.length > 0 && player1Units.length === 0) return 2;
    return 0; // Draw
  }

  /**
   * Process a single unit's turn
   */
  private processUnit(unit: Entity, deltaTime: number): void {
    const combatState = unit.getComponent(CombatState);
    const stats = unit.getComponent(Stats);
    const position = unit.getComponent(Position);
    const owner = unit.getComponent(Owner);

    if (!combatState || !stats || !position || !owner) return;

    // Update cooldown
    if (combatState.attackCooldown > 0) {
      (combatState as { attackCooldown: number }).attackCooldown -= deltaTime;
    }

    // Find or validate target
    let target = combatState.targetEntityId
      ? this.world.getEntity(combatState.targetEntityId)
      : null;

    // Validate target is still alive
    if (target) {
      const targetCombat = target.getComponent(CombatState);
      if (!targetCombat || targetCombat.isDead) {
        target = null;
        (combatState as { targetEntityId: string | null }).targetEntityId = null;
      }
    }

    // Find new target if needed
    if (!target) {
      target = this.targetingSystem.findTarget(unit, TargetingStrategy.Closest);
      if (target) {
        (combatState as { targetEntityId: string | null }).targetEntityId = target.id;
      }
    }

    if (!target) return; // No targets available

    // Check if in range
    if (this.targetingSystem.isInAttackRange(unit, target)) {
      // In range - attack if cooldown ready
      if (combatState.attackCooldown <= 0) {
        this.executeAttack(unit, target);
        (combatState as { attackCooldown: number }).attackCooldown = getAttackCooldown(stats);
      }
    } else {
      // Not in range - move towards target
      this.moveTowardsTarget(unit, target, deltaTime);
    }
  }

  /**
   * Execute an attack
   */
  private executeAttack(attacker: Entity, target: Entity): void {
    const attackerStats = attacker.getComponent(Stats);
    const attackerMesh = attacker.getComponent(UnitMesh);
    const targetStats = target.getComponent(Stats);
    const targetCombat = target.getComponent(CombatState);
    const targetMesh = target.getComponent(UnitMesh);

    if (!attackerStats || !targetStats || !targetCombat) return;

    // Play attack animation
    if (attackerMesh) {
      this.unitRenderer.playAttackAnimation(attackerMesh.meshId);
    }

    // Calculate and apply damage
    const damageResult = calculatePhysicalDamage(attackerStats, targetStats);
    const died = applyDamage(targetStats, damageResult.finalDamage);

    console.log(`[CombatSystem] ${attacker.id} attacks ${target.id} for ${damageResult.finalDamage} damage${damageResult.isCritical ? ' (CRITICAL!)' : ''}`);

    // Update health bar
    if (targetMesh) {
      const healthPercent = targetStats.currentHealth / targetStats.maxHealth;
      this.unitRenderer.updateHealthBar(targetMesh.meshId, healthPercent);
    }

    // Emit attack event
    this.eventBus.emit(CombatEvents.UNIT_ATTACKED, {
      attackerId: attacker.id,
      targetId: target.id,
      damage: damageResult.finalDamage,
      isCritical: damageResult.isCritical,
    } as UnitAttackedEvent);

    // Handle death
    if (died) {
      this.handleUnitDeath(target, attacker.id);
    }
  }

  /**
   * Move unit towards target
   */
  private moveTowardsTarget(
    unit: Entity,
    target: Entity,
    _deltaTime: number
  ): void {
    const position = unit.getComponent(Position);
    const stats = unit.getComponent(Stats);
    const combatState = unit.getComponent(CombatState);
    const unitMesh = unit.getComponent(UnitMesh);
    const targetPos = target.getComponent(Position);

    if (!position || !stats || !combatState || !targetPos) return;

    // Get blocked coordinates
    const blocked = this.targetingSystem.getBlockedCoords();
    // Remove own position from blocked
    blocked.delete(coordToKey({ q: position.q, r: position.r }));

    // Find path to attack range
    const path = this.pathfindingSystem.findPathToAttackRange(
      { q: position.q, r: position.r },
      { q: targetPos.q, r: targetPos.r },
      stats.attackRange,
      blocked
    );

    if (!path || path.length === 0) return;

    // Check if we should move this frame
    if (!combatState.isMoving) {
      const nextTile = path[0];

      // Check if next tile is still free
      if (this.hexGrid.isOccupied(nextTile)) {
        // Path blocked, recalculate next frame
        return;
      }

      // Update position
      this.hexGrid.setOccupant({ q: position.q, r: position.r }, null);

      const newPosition = new Position({ q: nextTile.q, r: nextTile.r });
      unit.removeComponent(Position);
      unit.addComponent(newPosition);

      this.hexGrid.setOccupant(nextTile, unit.id);

      // Animate mesh
      if (unitMesh) {
        this.unitRenderer.setTargetPosition(unitMesh.meshId, nextTile);
      }

      this.eventBus.emit(CombatEvents.UNIT_MOVED, {
        entityId: unit.id,
        from: { q: position.q, r: position.r },
        to: nextTile,
      });
    }
  }

  /**
   * Handle unit death
   */
  private handleUnitDeath(unit: Entity, killerId: string | null): void {
    const combatState = unit.getComponent(CombatState);
    const position = unit.getComponent(Position);
    const unitMesh = unit.getComponent(UnitMesh);

    if (!combatState) return;

    // Mark as dead
    (combatState as { isDead: boolean }).isDead = true;

    // Clear grid occupancy
    if (position) {
      this.hexGrid.setOccupant({ q: position.q, r: position.r }, null);
    }

    // Play death animation
    if (unitMesh) {
      this.dyingUnitsCount++;
      console.log(`[CombatSystem] Starting death animation for ${unit.id}. Dying units count: ${this.dyingUnitsCount}`);

      this.unitRenderer.playDeathAnimation(unitMesh.meshId, () => {
        // Animation complete callback
        this.dyingUnitsCount--;
        console.log(`[CombatSystem] Death animation finished for ${unit.id}. Dying units count: ${this.dyingUnitsCount}`);
      });
    }

    this.eventBus.emit(CombatEvents.UNIT_DIED, {
      entityId: unit.id,
      killerId,
    } as UnitDiedEvent);

    console.log('Unit died:', unit.id);
  }

  /**
   * Get combat statistics
   */
  getCombatStats(): { combatTime: number; speed: CombatSpeed } {
    return {
      combatTime: this.combatTime,
      speed: this.combatSpeed,
    };
  }
}
